/**
 * driverCriteriaAlignment — the consumption side of the StrategicDriver → criteria edge.
 *
 * StrategicDriver has carried `weight` ("should sum to 100 across all drivers")
 * and `linkedCriteriaIds` ("drivers influence how we evaluate priorities") since
 * Workstream 5, but nothing ever read either: driver weights reached no scoring
 * path, and criteria selection never knew which drivers it served. These helpers
 * close that gap for the S3 criteria surface:
 *
 *   - computeDriverCriteriaCoverage: which active drivers are represented by the
 *     chosen criteria, which are not, and which chosen criteria serve no driver.
 *   - proposeCriteriaWeightsFromDrivers: driver weights → proposed criteria
 *     weights. A PROPOSAL only — a person reviews and applies it; nothing here
 *     writes state, and applying changes weights on the existing criteria set
 *     without adding or removing a criterion, so workflow criteria governance
 *     (locked attributes, locked ranges — see workflowCriteriaGovernance) cannot
 *     be violated by construction.
 *
 * Id space: linkedCriteriaIds hold workspace attribute keys — the one id that is
 * stable across track → session → values (see sessionCriteriaFromTrack; a
 * track-inherited or system-pulled criterion's id IS the attribute key).
 * Session-authored `crit_*` ids exist only inside one event, so links to them
 * are matched when present but should never be authored.
 */

import type { StrategicDriver } from '../types/prioritization/strategy';
import type { CriterionDef } from '../features/session-engine-v3/types';
import { normalizeCriteriaWeights } from './sessionCriteriaFromTrack';

export interface DriverCoverageRow {
  driverId: string;
  driverName: string;
  /** The driver's strategic weight (drivers should sum to 100). */
  weight: number;
  /** Chosen criteria ids that serve this driver (linked ∩ chosen). */
  matchedCriteriaIds: string[];
  /** The driver links to no criteria at all — a linking gap, not a selection gap. */
  unlinked: boolean;
}

export interface DriverCriteriaCoverage {
  /** Active drivers in display order. */
  rows: DriverCoverageRow[];
  /** Rows with at least one matched criterion. */
  representedCount: number;
  activeDriverCount: number;
  /** Sum of active driver weight that the chosen criteria represent. */
  representedDriverWeight: number;
  /** Sum of all active driver weight (may not be 100 — drivers are hand-weighted). */
  totalActiveDriverWeight: number;
  /** Chosen criteria ids no active driver links to. */
  unservedCriteriaIds: string[];
}

const activeInOrder = (drivers: readonly StrategicDriver[]): StrategicDriver[] =>
  drivers.filter((d) => d.isActive).sort((a, b) => a.order - b.order);

/**
 * Which strategic drivers do the chosen criteria represent?
 * Pure set math over ids — inactive drivers are out of scope entirely.
 */
export function computeDriverCriteriaCoverage(
  drivers: readonly StrategicDriver[],
  criteria: readonly Pick<CriterionDef, 'id'>[],
): DriverCriteriaCoverage {
  const chosen = new Set(criteria.map((c) => c.id));
  const active = activeInOrder(drivers);

  const rows: DriverCoverageRow[] = active.map((d) => ({
    driverId: d.id,
    driverName: d.name,
    weight: d.weight,
    matchedCriteriaIds: d.linkedCriteriaIds.filter((id) => chosen.has(id)),
    unlinked: d.linkedCriteriaIds.length === 0,
  }));

  const linkedAnywhere = new Set(active.flatMap((d) => d.linkedCriteriaIds));
  const represented = rows.filter((r) => r.matchedCriteriaIds.length > 0);

  return {
    rows,
    representedCount: represented.length,
    activeDriverCount: active.length,
    representedDriverWeight: represented.reduce((sum, r) => sum + r.weight, 0),
    totalActiveDriverWeight: rows.reduce((sum, r) => sum + r.weight, 0),
    unservedCriteriaIds: criteria.map((c) => c.id).filter((id) => !linkedAnywhere.has(id)),
  };
}

export interface CriterionWeightProposal {
  id: string;
  name: string;
  /** Current weight, normalized to a percentage for honest comparison. */
  currentWeight: number;
  /** Proposed weight (percentages summing to 100). */
  proposedWeight: number;
}

export interface DriverWeightProposal {
  /** Same ids, names, and order as the criteria passed in — weights only differ. */
  proposals: CriterionWeightProposal[];
  /** Active drivers whose weight could not flow (no matched criteria). */
  unrepresentedDriverIds: string[];
  /** Criteria proposed at 0% — no represented driver links to them. */
  zeroedCriteriaIds: string[];
}

/**
 * Distribute each active driver's weight equally across its matched chosen
 * criteria (a criterion serving several drivers accumulates from each), then
 * normalize to whole percentages summing to 100.
 *
 * Returns null when no active driver matches any chosen criterion — there is
 * nothing honest to propose. Drivers with no match are excluded and reported
 * (their weight cannot flow through criteria that were not chosen).
 */
export function proposeCriteriaWeightsFromDrivers(
  drivers: readonly StrategicDriver[],
  criteria: readonly CriterionDef[],
): DriverWeightProposal | null {
  if (criteria.length === 0) return null;
  const chosen = new Set(criteria.map((c) => c.id));
  const active = activeInOrder(drivers);

  const raw = new Map<string, number>();
  const unrepresentedDriverIds: string[] = [];
  let anyFlow = false;

  for (const d of active) {
    const matched = d.linkedCriteriaIds.filter((id) => chosen.has(id));
    if (matched.length === 0) {
      unrepresentedDriverIds.push(d.id);
      continue;
    }
    // A matched driver with weight 0 contributes nothing; it must not turn an
    // all-zero grid into an invented even split downstream.
    if (d.weight > 0) anyFlow = true;
    const share = d.weight / matched.length;
    for (const id of matched) raw.set(id, (raw.get(id) ?? 0) + share);
  }
  if (!anyFlow) return null;

  const proposed = normalizeCriteriaWeights(
    criteria.map((c) => ({ ...c, weight: raw.get(c.id) ?? 0 })),
  );

  // normalizeCriteriaWeights parks rounding drift on the LAST entry; if that
  // entry received no driver weight, drift would invent strategic weight for a
  // criterion no driver serves. Move any such drift to the heaviest entry.
  const last = proposed[proposed.length - 1];
  if (last && !raw.has(last.id) && last.weight > 0) {
    const heaviest = proposed.reduce((best, c) => (raw.has(c.id) && c.weight > best.weight ? c : best), {
      id: '',
      name: '',
      weight: -1,
    });
    if (heaviest.id) {
      heaviest.weight += last.weight;
      last.weight = 0;
    }
  }

  const current = normalizeCriteriaWeights(criteria);
  const currentById = new Map(current.map((c) => [c.id, c.weight]));
  const proposedById = new Map(proposed.map((c) => [c.id, c.weight]));

  return {
    proposals: criteria.map((c) => ({
      id: c.id,
      name: c.name,
      currentWeight: currentById.get(c.id) ?? 0,
      proposedWeight: proposedById.get(c.id) ?? 0,
    })),
    unrepresentedDriverIds,
    zeroedCriteriaIds: criteria.map((c) => c.id).filter((id) => (proposedById.get(id) ?? 0) === 0),
  };
}
